import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Eye, AlertTriangle } from 'lucide-react';
import {
  creativeWorkbenchApi,
  type WorkbenchCoverageResponse,
} from '@/services/api';
import { useCreativeWorkbench } from '../CreativeWorkbenchContext';

const CURRENCY = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  maximumFractionDigits: 0,
});

export function ReviewedCoverageView() {
  const navigate = useNavigate();
  const { clientUid } = useCreativeWorkbench();
  const [data, setData] = useState<WorkbenchCoverageResponse | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!clientUid) return;
    let cancelled = false;
    setLoading(true);
    creativeWorkbenchApi
      .getReviewedCoverage({ clientUid, top: 50 })
      .then((p) => {
        if (!cancelled) setData(p);
      })
      .catch(() => {
        if (!cancelled) setData(null);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [clientUid]);

  return (
    <div className="space-y-4">
      <header className="flex items-center gap-3">
        <button
          onClick={() => navigate('/creative/performance')}
          className="p-1.5 text-slate-500 hover:text-teal-600 rounded hover:bg-slate-100"
          aria-label="Back"
        >
          <ArrowLeft size={16} />
        </button>
        <div>
          <div className="text-[10px] font-mono uppercase text-slate-400">
            Reviewed coverage
          </div>
          <h1 className="text-xl font-bold text-navy-900 flex items-center gap-2">
            <Eye size={18} className="text-teal-500" />
            Where are we spending blind?
          </h1>
        </div>
      </header>

      {loading && !data && (
        <div className="text-sm text-slate-400">Scanning ad inventory...</div>
      )}

      {data && (
        <>
          <div className="grid grid-cols-3 gap-3">
            <CoverageTile
              label="Total spend"
              value={CURRENCY.format(data.total_spend)}
            />
            <CoverageTile
              label="Reviewed spend"
              value={CURRENCY.format(data.reviewed_spend)}
              accent
            />
            <CoverageTile
              label="Coverage"
              value={`${data.reviewed_pct.toFixed(1)}%`}
              accent
            />
          </div>

          <section className="bg-white border border-slate-200 rounded-xl p-4 space-y-3">
            <header className="flex items-center justify-between">
              <h2 className="text-sm font-bold text-navy-900 flex items-center gap-1.5">
                <AlertTriangle size={14} className="text-amber-500" />
                Top unreviewed ads by spend
              </h2>
              <span className="text-[10px] font-mono text-slate-400">
                {data.unreviewed_count} ads waiting on a fit signal
              </span>
            </header>
            {data.unreviewed_top.length === 0 ? (
              <div className="text-xs text-slate-400 py-6 text-center">
                Every active ad has a fit signal. Nice — you're operating with
                full creative coverage.
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-[11px]">
                  <thead>
                    <tr className="text-left text-slate-400 border-b border-slate-100">
                      <th className="font-mono uppercase pr-2 py-1">Ad</th>
                      <th className="font-mono uppercase px-2 py-1">Campaign</th>
                      <th className="font-mono uppercase px-2 py-1">Persona delivered to</th>
                      <th className="font-mono uppercase px-2 py-1 text-right">Spend</th>
                      <th className="font-mono uppercase px-2 py-1 text-right">CTR</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.unreviewed_top.map((ad) => (
                      <tr
                        key={ad.ad_id}
                        className="border-b border-slate-50 hover:bg-amber-50"
                      >
                        <td className="pr-2 py-2 max-w-xs">
                          <div
                            className="font-medium text-navy-900 truncate"
                            title={ad.ad_name ?? ad.ad_id}
                          >
                            {ad.ad_name ?? ad.ad_id}
                          </div>
                        </td>
                        <td className="px-2 py-2 text-slate-500 truncate max-w-xs">
                          {ad.campaign_name}
                        </td>
                        <td className="px-2 py-2 text-slate-500">
                          {ad.actual_persona}
                        </td>
                        <td className="px-2 py-2 text-right font-mono text-navy-900">
                          {CURRENCY.format(ad.spend)}
                        </td>
                        <td className="px-2 py-2 text-right font-mono text-slate-500">
                          {(ad.ctr * 100).toFixed(2)}%
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>
        </>
      )}
    </div>
  );
}

function CoverageTile({
  label,
  value,
  accent,
}: {
  label: string;
  value: string;
  accent?: boolean;
}) {
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-3">
      <div className="text-[10px] font-mono uppercase text-slate-400">{label}</div>
      <div
        className={`text-2xl font-bold tabular-nums ${
          accent ? 'text-teal-600' : 'text-navy-900'
        }`}
      >
        {value}
      </div>
    </div>
  );
}
